import BaseAuthProvider from './BaseAuthProvider'
import AuthService from '../service'
import { AuthProviders } from '../constants/authProviders'

/**
 * SSO Authentication Provider
 * Single Sign-On integration for enterprise customers
 * Used via /sso route with integration_id and token parameters
 */
export class SsoAuthProvider extends BaseAuthProvider {
    getMethod() {
        return AuthProviders.SSO
    }

    /**
     * SSO provider is enabled when:
     * 1. SSO is in .env providers (VUE_APP_AUTH_PROVIDERS)
     * 2. ?t=xxx&k=yyy parameters are present in URL
     * This allows auto-prioritization of SSO auth when the parameters are detected
     *
     * @param {Object} context - Context with queryParams and envProviders
     * @returns {boolean} True if enabled in .env AND ?t=xxx&k=yyy are present in URL
     */
    isEnabled(context) {
        // 1. Check .env (base requirement)
        if (!context.envProviders.includes(this.getMethod())) {
            console.log('[SsoAuthProvider] Not in envProviders:', context.envProviders)
            return false
        }

        // 2. Check if both ?t and ?k parameters exist in query
        const hasParams = !!(context.queryParams && context.queryParams.t && context.queryParams.k)
        console.log('[SsoAuthProvider] isEnabled check:', {
            hasT: !!(context.queryParams && context.queryParams.t),
            hasK: !!(context.queryParams && context.queryParams.k),
            enabled: hasParams
        })
        return hasParams
    }

    /**
     * Authenticate via SSO integration
     * @param {Object} credentials
     * @param {string} credentials.integration_id - SSO integration identifier
     * @param {string} credentials.token - SSO token from external system
     * @returns {Promise<{token: string, tokenType: string, picacode?: string}>}
     *   Returns authentication token plus any metadata from backend (e.g., picacode for direct album access)
     */
    async authenticate({ integration_id, token }) {
        if (!integration_id || !token) {
            throw new Error('SSO authentication requires integration_id and token')
        }

        const response = await AuthService.getTokenFromSsoLogin(integration_id, token)

        // Return token plus all metadata from API response (e.g., picacode)
        return {
            token: response.data.token,
            tokenType: 'Token',
            ...response.data  // Include all additional metadata (picacode, etc.)
        }
    }

    /**
     * No alternatives for SSO
     * It's a direct enterprise integration
     */
    getAlternatives(enabledProviders) {
        return []
    }
}

export default SsoAuthProvider
